import {
  buildVestSchedule,
  fmt,
  type CalcSettings,
  type Opportunity,
  type OpportunityResult,
  type SensitivityResult,
  type TaxRates
} from '@/engines/job-offer-engine';
import type { FieldExplainerContent } from './types';

export type JobOfferOfferExplainerContext = {
  offer: Opportunity;
  result: OpportunityResult;
  settings: CalcSettings;
  /** Rates after the preset and filing-status adjustment have been resolved. */
  taxRates: TaxRates;
};

export type JobOfferGlobalExplainerContext = {
  settings: CalcSettings;
  taxRates: TaxRates;
  sensitivity: {
    bearMult: number;
    bullMult: number;
    probBear: number;
    probBase: number;
    probBull: number;
  };
  sensitivityOutcome: SensitivityResult | null;
  /** Name of the offer the sensitivity panel is currently pointed at. */
  sensitivityOfferName: string | null;
  brief: {
    winnerName: string;
    runnerUpName: string | null;
    spread: number;
    winnerNetWorth: number;
    winnerCumCash: number;
    winnerCashShare: number;
    winnerIlliquid: number;
    downsideGap: number | null;
  } | null;
  offers: Array<{ name: string; netWorth: number; cumCash: number }>;
};

const RATE_LABELS: Record<string, string> = {
  ord: 'ordinary',
  ltcg: 'long-term capital gains',
  isoStateCity: 'ISO state and city',
  isoAmt: 'ISO federal AMT',
  cashEff: 'cash effective'
};

function rate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function ratio(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, '') || '0';
}

function n(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** Per-share prices need cents; `fmt` rounds them to whole dollars. */
function money(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Math.abs(value) < 1000 ? `$${value.toFixed(2)}` : fmt(value);
}

/** Explainers for one offer card's inputs and its net-worth result tile. */
export function getJobOfferOfferExplainers(ctx: JobOfferOfferExplainerContext): FieldExplainerContent[] {
  const { offer, result, settings, taxRates } = ctx;
  const horizon = settings.timeHorizon;
  const years = result.years;
  const last = years[years.length - 1];
  const first = years[0];
  const valPath = result.meta.valPath;
  const fmvPath = result.meta.fmvPath;

  const grantRef = offer.grantDateValuation > 0 ? offer.grantDateValuation : 1;
  const finalVal = valPath[horizon - 1] ?? grantRef;
  const finalMult = finalVal / grantRef;

  const vestSchedule = offer.rsuEnabled
    ? buildVestSchedule(offer.rsuTotalValue, offer.rsuVestYears, offer.rsuCliffMonths, offer.rsuVestFrequency, horizon)
    : new Array(horizon).fill(0);
  const vestedInHorizon = vestSchedule.reduce((s, v) => s + v, 0);
  const grantTruncated = offer.rsuEnabled && offer.rsuVestYears > horizon;

  const y1Base = offer.baseSalary;
  const finalBase = offer.baseSalary * Math.pow(1 + offer.baseSalaryGrowthPct, horizon - 1);
  const y1Bonus = y1Base * offer.targetBonusPct;
  const cumCash = last?.cumCash ?? 0;
  const netWorth = last?.netWorth ?? 0;
  const illiquid = Math.max(0, netWorth - cumCash);
  const cashShare = netWorth > 0 ? cumCash / netWorth : 0;

  const vestPerYrOptions = offer.optionVestYears > 0 ? Math.floor(offer.optionCount / offer.optionVestYears) : 0;
  const spreadNow = Math.max(0, offer.currentFMV - offer.strikePrice);
  const finalFmv = fmvPath[horizon - 1] ?? offer.currentFMV;

  const entries: FieldExplainerContent[] = [];

  // ── Cash ──
  entries.push(
    {
      id: 'baseSalary',
      kind: 'input',
      label: 'Base salary',
      plain: `Year-one salary for ${offer.name}. It is the only figure here that is certain — everything downstream of the equity section is a projection.`,
      formula: `year 1  ${fmt(y1Base)}\nyear ${horizon}  ${fmt(y1Base)} × (1 + ${ratio(offer.baseSalaryGrowthPct)})^${horizon - 1} = ${fmt(finalBase)}\ntaxed at the cash effective rate ${rate(taxRates.cashEff)}\nyear 1 take-home ${fmt(first?.cashNet ?? 0)}`,
      feeds: 'Cumulative cash, the cash share of net worth, and the NPV column when discounting is on.',
      assumption:
        'Cash is taxed at a single blended effective rate, not through brackets. It is the same rate at every salary level, so two offers that differ only in cash keep the same take-home percentage.'
    },
    {
      id: 'baseSalaryGrowthPct',
      kind: 'input',
      label: 'Raise / yr',
      plain:
        'Compound annual raise applied to base salary. Entered as a decimal, so 0.05 means five percent. Bonus rides on top of the grown base, so a raise lifts both.',
      formula: `base in year y = ${fmt(y1Base)} × (1 + ${ratio(offer.baseSalaryGrowthPct)})^(y − 1)\nyear ${horizon} base = ${fmt(finalBase)}\nover ${horizon} years that is ${fmt(finalBase - y1Base)} more than year 1`,
      feeds: 'Every year of cash comp, and through it cumulative cash and the cash share of net worth.',
      assumption:
        'A guaranteed, identical raise every year with no promotion jumps and no down years. Nothing here adjusts for inflation, so a 3% raise in a 3% inflation world reads as growth on this page.'
    },
    {
      id: 'signingBonus',
      kind: 'input',
      label: 'Signing bonus',
      plain: 'A one-time payment counted entirely in year one. It never repeats and it does not raise the bonus base.',
      formula: `year 1 cash = base ${fmt(y1Base)} + bonus ${fmt(y1Bonus)} + signing ${fmt(offer.signingBonus)}\n  = ${fmt(first?.cashGross ?? 0)} gross\n  = ${fmt(first?.cashNet ?? 0)} after ${rate(taxRates.cashEff)}`,
      feeds: 'Year-one cash only. On a long horizon it is the input that matters least to the final ranking.',
      assumption:
        'No clawback is modeled. A signing bonus you would have to repay on early departure is worth less than the number here implies, and this calculator has no way to express that.'
    },
    {
      id: 'targetBonusPct',
      kind: 'input',
      label: 'Target bonus',
      plain:
        'Annual bonus as a fraction of that year’s base salary, so 0.15 is fifteen percent. It grows automatically as base salary grows.',
      formula: `year 1  ${fmt(y1Base)} × ${ratio(offer.targetBonusPct)} = ${fmt(y1Bonus)}\nyear ${horizon}  ${fmt(finalBase)} × ${ratio(offer.targetBonusPct)} = ${fmt(finalBase * offer.targetBonusPct)}`,
      feeds: 'Gross cash each year, and therefore cumulative cash.',
      assumption:
        'Target is treated as paid in full every year. Real bonuses swing with company and individual performance, and the swing is usually wider at the companies whose equity numbers look most exciting.'
    }
  );

  // ── RSU ──
  if (offer.rsuEnabled) {
    entries.push(
      {
        id: 'rsuTotalValue',
        kind: 'input',
        label: 'Total grant value',
        plain:
          'The headline dollar value of the initial RSU grant, priced at the grant-date valuation. Internally it is held as a share-like claim, so it moves with the valuation path rather than staying fixed at this number.',
        formula: `${fmt(offer.rsuTotalValue)} at the grant-date reference of ${ratio(grantRef)}\nvesting in the horizon: ${vestSchedule.map((v) => fmt(v)).join(' · ')}\nvalued at year ${horizon} (${ratio(finalMult)}× grant): ${fmt(vestedInHorizon * finalMult)}`,
        feeds: 'The vest schedule, the taxable event each vest year, and whatever lands in the sellable pool.',
        assumption: grantTruncated
          ? `Your ${offer.rsuVestYears}-year vest runs past the ${horizon}-year horizon, so only ${fmt(vestedInHorizon)} of the ${fmt(offer.rsuTotalValue)} grant vests inside the window. The rest is not counted anywhere on this page.`
          : 'Priced in grant-date dollars. If the offer letter quotes a share count instead, convert at the price they used, or the appreciation math starts from the wrong base.'
      },
      {
        id: 'rsuVestYears',
        kind: 'input',
        label: 'Vest years',
        plain: 'How long the initial grant takes to vest in full. Longer vesting spreads the same dollars thinner per year and pushes more of the grant past the horizon.',
        formula: `${fmt(offer.rsuTotalValue)} / ${offer.rsuVestYears} years = ${fmt(offer.rsuTotalValue / Math.max(1, offer.rsuVestYears))} per year\nhorizon is ${horizon} years\nvests inside the horizon: ${fmt(vestedInHorizon)} of ${fmt(offer.rsuTotalValue)}`,
        feeds: 'The per-year vest schedule and the pipeline of unvested value shown in the annual detail tab.',
        assumption: grantTruncated
          ? 'Vesting continues past the end of the horizon. Value that has not vested by the final modeled year is excluded from net worth rather than pulled forward.'
          : undefined
      },
      {
        id: 'rsuCliffMonths',
        kind: 'input',
        label: 'Cliff (months)',
        plain:
          'Months before anything vests. At the cliff the whole backed-up amount releases at once, then vesting continues at the chosen frequency.',
        formula: `${offer.rsuCliffMonths} months × ${fmt(offer.rsuTotalValue / Math.max(1, offer.rsuVestYears * 12))}/month\n  = ${fmt((offer.rsuTotalValue / Math.max(1, offer.rsuVestYears * 12)) * offer.rsuCliffMonths)} released at the cliff\nyear 1 total vest: ${fmt(vestSchedule[0] ?? 0)}`,
        feeds: 'Which calendar year each vest lands in, which in turn decides the valuation it is recognised at.',
        assumption:
          'The cliff only delays value; it never forfeits any. Leaving before the cliff would forfeit the whole backed-up amount, and this page has no departure date to model that with.'
      },
      {
        id: 'rsuVestFrequency',
        kind: 'input',
        label: 'Vest frequency',
        plain:
          'How often units release after the cliff. It shifts value between calendar years at the margins; on an annual view the difference is small unless the valuation is moving fast.',
        formula: `monthly → 1-month ticks · quarterly → 3 · annual → 12\nselected: ${offer.rsuVestFrequency}\nyear-by-year vest: ${vestSchedule.map((v) => fmt(v)).join(' · ')}`,
        feeds: 'The bucketing of vests into years, and therefore which year’s valuation each vest is priced at.'
      },
      {
        id: 'rsuAnnualRefresh',
        kind: 'input',
        label: 'Annual refresh value',
        plain:
          'A new grant of this size issued at the end of every year, each vesting over the same number of years as the initial grant. Refreshes are priced at that year’s valuation, not the original grant-date valuation.',
        formula:
          offer.rsuAnnualRefresh > 0
            ? `each refresh: ${fmt(offer.rsuAnnualRefresh)} over ${offer.rsuVestYears} years\n  = ${fmt(offer.rsuAnnualRefresh / Math.max(1, offer.rsuVestYears))} per year, priced at that year’s valuation\nrefreshes granted in the horizon: ${horizon}`
            : `${fmt(0)} — no refresh modeled\na refresh of ${fmt(offer.rsuTotalValue / 4)} a year would add roughly\n${fmt(offer.rsuTotalValue / 4 / Math.max(1, offer.rsuVestYears))} of new vesting per year`,
        feeds: 'Vest value in every year after the first, and the pipeline figure in the annual detail tab.',
        assumption:
          'Refreshes are assumed to arrive on schedule at full value forever. In practice they are discretionary, performance-linked, and the first thing trimmed in a bad year — which is exactly when the valuation path is also disappointing.'
      }
    );
  }

  // ── Options ──
  if (offer.optionsEnabled) {
    entries.push(
      {
        id: 'optionCount',
        kind: 'input',
        label: 'Option count',
        plain:
          'Total options in the grant. They vest in equal whole-share slices across the vest years, and their worth is entirely the gap between share price and strike.',
        formula: `${n(offer.optionCount)} options / ${offer.optionVestYears} years\n  = ${n(vestPerYrOptions)} vesting per year\nintrinsic value today: ${n(offer.optionCount)} × (${money(offer.currentFMV)} − ${money(offer.strikePrice)}) = ${fmt(offer.optionCount * spreadNow)}`,
        feeds: 'Vested share counts, exercise cost, held value, and the ceiling on what any tender window can sell.',
        assumption:
          'The per-year slice is rounded down to whole shares, so a count that does not divide evenly loses a share or two across the schedule. No option-grant cliff is applied: the first slice vests in year one.'
      },
      {
        id: 'strikePrice',
        kind: 'input',
        label: 'Strike price',
        plain:
          'What you pay per share to exercise. Every dollar of strike is a dollar of upside you do not get, and cash you have to find if you exercise and hold.',
        formula: `spread today = ${money(offer.currentFMV)} − ${money(offer.strikePrice)} = ${money(spreadNow)} per share\nexercising all ${n(offer.optionCount)} costs ${fmt(offer.optionCount * offer.strikePrice)}\nat year ${horizon} FMV of ${money(finalFmv)} the spread is ${money(Math.max(0, finalFmv - offer.strikePrice))} per share`,
        feeds: 'Exercise cost, taxable spread at exercise, intrinsic value of unexercised options, and tender proceeds.',
        assumption:
          'Options are worth nothing at or below strike, and this model floors the spread at zero rather than letting it go negative. A share price below strike simply makes the whole grant a zero on this page.'
      },
      {
        id: 'currentFMV',
        kind: 'input',
        label: 'Current FMV',
        plain:
          'Today’s fair market value per share — the 409A price, not the preferred price investors paid. It is the base the share price grows from along the valuation path.',
        formula: `FMV in year y = ${money(offer.currentFMV)} × (valuation in year y / ${ratio(grantRef)})${offer.dilutionEnabled ? ` × (1 − ${ratio(offer.annualDilutionPct)})^y` : ''}\nyear 1  ${money(fmvPath[0] ?? offer.currentFMV)}\nyear ${horizon}  ${money(finalFmv)}`,
        feeds: 'Every option valuation on the page, the ISO $100k split, and tender pricing.',
        assumption:
          'Using the preferred-round price here rather than the 409A will overstate the grant, sometimes by several times. Common shares sit behind every liquidation preference in the stack, and none of that structure is modeled.'
      },
      {
        id: 'optionVestYears',
        kind: 'input',
        label: 'Option vest years',
        plain: 'How many years the option grant takes to vest. Options vest in equal whole-share slices, one slice per year, starting in year one.',
        formula: `${n(offer.optionCount)} / ${offer.optionVestYears} = ${n(vestPerYrOptions)} shares per year\nvested by year ${horizon}: ${n(Math.min(offer.optionCount, vestPerYrOptions * horizon))} shares`,
        feeds: 'The share count available to exercise or tender in each year.',
        assumption:
          'No option cliff is applied even when one is set elsewhere in the record: the first slice vests at the end of year one. If your grant has a one-year cliff and annual vesting after it, that happens to match; monthly-after-cliff grants will vest slightly earlier here than in reality.'
      },
      {
        id: 'isoFraction',
        kind: 'input',
        label: 'ISO fraction',
        plain:
          'Share of the grant treated as incentive stock options; the rest are NSOs. ISOs avoid ordinary income at exercise but create an AMT preference item, so the model taxes them differently.',
        formula: `requested ISO share: ${ratio(offer.isoFraction)}\n§422(d) caps ISOs first exercisable per year at $100,000 of grant-date FMV\n  cap = $100,000 / ${money(offer.currentFMV)} = ${n(offer.currentFMV > 0 ? Math.floor(100000 / offer.currentFMV) : vestPerYrOptions)} shares/yr\nNSO spread taxed at ${rate(taxRates.ord)}; ISO spread at ${rate(taxRates.isoStateCity)} state and city, or ${rate(taxRates.isoAmt)} federal AMT, whichever is higher`,
        feeds: 'The tax charged when options are exercised, and the order the tender window sells in.',
        assumption:
          'AMT is applied as a flat rate on the ISO spread, not through the real AMT calculation with its exemption and the income band where that exemption is withdrawn. For a modest spread the real bill is often lower; for a large one this understates how punishing exercise-and-hold can be.'
      }
    );
  }

  // ── Liquidity and valuation ──
  entries.push(
    {
      id: 'liquidityType',
      kind: 'input',
      label: 'Liquidity type',
      plain:
        'The single most consequential switch on the card. It decides whether vested equity is money you could spend, or a number on a page you cannot touch.',
      formula: offer.rsuDoubleTrigger
        ? `with double-trigger RSUs ON:\npublic      → vests are taxed as income and land in a sellable pool\nplanned IPO → vests stack up untaxed until the IPO year\ntender only → vests stay locked; tender windows release value\nno liquidity→ vests taxed on the public schedule but never sold\n\nselected: ${offer.liquidityType}`
        : `with double-trigger RSUs OFF:\nevery vest is taxed as ordinary income in its vest year,\nwhatever the liquidity setting — IPO stacking and\ntender-window releases only apply with double-trigger on\n\nselected: ${offer.liquidityType}`,
      feeds: 'Whether each vest is a taxable event now or later, whether the pool can be sold, and how much of net worth is illiquid.',
      assumption:
        offer.liquidityType === 'ipo'
          ? 'The IPO is treated as certain, in exactly the year you named. Naming a year is a forecast, not a plan, and nothing here expresses the odds that it slips or never happens.'
          : offer.liquidityType === 'public'
            ? 'Public here means you can sell whenever you like. Real trading windows, blackout periods, and 10b5-1 plans are not modeled, so this is the most generous reading of liquidity.'
            : offer.liquidityType === 'tender-only'
              ? 'With no IPO in the model, tender windows are the only way value ever reaches your bank account. Whatever those windows do not sell stays paper for the whole horizon.'
              : 'With no liquidity, vests are still taxed on the public schedule but nothing can ever be sold. That combination — owing tax on stock you cannot sell — is the risk this setting exists to show.'
    },
    {
      id: 'grantDateValuation',
      kind: 'input',
      label: 'Grant-date valuation',
      plain:
        'The reference point every equity number on this card is measured against. Its unit is whatever you choose — company valuation in billions, or dollars per share — as long as the year-by-year path uses the same unit.',
      formula: `multiplier in year y = valuation in year y / ${ratio(grantRef)}\nyear ${horizon}: ${ratio(finalVal)} / ${ratio(grantRef)} = ${ratio(finalMult)}×\nequity is worth ${ratio(finalMult)}× its grant-date value by then`,
      feeds: 'Every appreciation multiple on the card: RSU value, FMV per share, and the sellable pool.',
      assumption:
        'Only the ratio matters, never the absolute number, so mixing units between this field and the year-by-year path silently corrupts every equity figure. A value of zero or below falls back to a reference of 1, which reads the valuation path as plain multipliers.'
    },
    {
      id: 'valuationPath',
      kind: 'input',
      label: 'Year-by-year valuation path',
      plain:
        'Your forecast of what the company is worth at the end of each year. It drives every equity number on this card, and it is the input you know least about.',
      formula: `${valPath
        .slice(0, horizon)
        .map((v, i) => `Y${i + 1}  ${ratio(v)}  (${ratio(v / grantRef)}× grant)`)
        .join('\n')}`,
      feeds: 'RSU vest value, share FMV, tender pricing, the sellable pool, and the whole net-worth line.',
      assumption:
        'A single deterministic path with no distribution around it. This is the assumption doing the most work on the page, which is what the breakeven and sensitivity tabs exist to stress.'
    },
    {
      id: 'existingPoolSellFrac',
      kind: 'input',
      label: 'Post-liquidity sell frac',
      plain:
        'The fraction of your sellable pool you sell each year once the stock is liquid. It converts paper into cash and triggers capital gains tax on the appreciation.',
      formula:
        offer.existingPoolSellFrac > 0
          ? `each year after liquidity: sell ${ratio(offer.existingPoolSellFrac)} of the pool\ngains taxed at ${rate(taxRates.ltcg)}\nyear ${horizon} (new grants + pre-existing equity):\n  proceeds ${fmt((last?.poolSale.proceeds ?? 0) + (last?.existingSale?.proceeds ?? 0))}, tax ${fmt((last?.poolSale.ltcgTax ?? 0) + (last?.existingSale?.ltcgTax ?? 0))}, net ${fmt((last?.poolSale.net ?? 0) + (last?.existingSale?.net ?? 0))}`
          : `${ratio(0)} — nothing is sold\nthe entire pool of ${fmt(last?.poolAfter ?? 0)} stays in stock,\nexposed to the valuation path to the last modeled year`,
      feeds: 'Cash proceeds, capital gains tax, the size of the remaining pool, and the cash share of net worth.',
      assumption:
        offer.liquidityType === 'ipo'
          ? `The same fraction also drains any pre-existing vested equity you entered. Lockup length is not modeled: the pool simply becomes sellable in year ${offer.ipoYear + 1}, whatever lockup the underwriters actually impose.`
          : 'The same fraction also drains any pre-existing vested equity you entered, every year, with no regard for trading windows or 10b5-1 plan timing.'
    }
  );

  if (offer.liquidityType === 'ipo') {
    entries.push({
      id: 'ipoYear',
      kind: 'input',
      label: 'IPO year',
      plain:
        'The year the stock becomes real. Under double-trigger vesting nothing is taxed before it, then the entire backlog is recognised as ordinary income in that one year at that year’s valuation.',
      formula: `IPO in year ${offer.ipoYear} at ${ratio(valPath[offer.ipoYear - 1] ?? finalVal)} (${ratio((valPath[offer.ipoYear - 1] ?? finalVal) / grantRef)}× grant)\nbacklog recognised: ${fmt(years[offer.ipoYear - 1]?.ipoTaxEvent ?? 0)}\ntax at ${rate(taxRates.ord)}: ${fmt((years[offer.ipoYear - 1]?.ipoTaxEvent ?? 0) * taxRates.ord)}\npool becomes sellable from year ${offer.ipoYear + 1}`,
      feeds: 'The single largest tax event on the page, the year paper turns into a sellable pool, and everything after it.',
      assumption:
        'The whole backlog is taxed at one flat ordinary rate. A recognition event that size would in reality push you into higher brackets for that year, so the tax shown is a floor, not an estimate.'
    });
  }

  if (offer.tenderEnabled) {
    entries.push(
      {
        id: 'tenderFirstYear',
        kind: 'input',
        label: 'First tender year',
        plain: 'The earliest year a secondary sale window opens. Before it, vested equity has no route to cash at all.',
        formula: `windows run from year ${offer.tenderFirstYear}${offer.liquidityType === 'ipo' ? ` up to the IPO in year ${offer.ipoYear}` : ` to year ${horizon}`}\ncadence: ${offer.tenderCadence} (${offer.tenderCadence === 'semiannual' ? '2 windows' : offer.tenderCadence === 'annual' ? '1 window' : 'no windows'} per year)`,
        feeds: 'When tender proceeds start showing up in cash, and how much paper value gets converted before any IPO.',
        assumption:
          'Tender windows are treated as guaranteed once they start. Real tenders are announced at the company’s discretion, are often oversubscribed, and can be cancelled outright in the years you would most want one.'
      },
      {
        id: 'tenderCapPct',
        kind: 'input',
        label: 'Cap (% of vested)',
        plain:
          'The share of your vested holdings you are allowed to sell in one window. It is applied to what remains at each window, so two windows in a year sell less than twice the cap.',
        formula: `each window sells ${ratio(offer.tenderCapPct)} of what is vested at that moment\n${offer.tenderCadence === 'semiannual' ? `two windows leave ${ratio((1 - offer.tenderCapPct) ** 2)} of the starting position, not ${ratio(1 - 2 * offer.tenderCapPct)}` : 'one window per year'}\nyear ${horizon} tender proceeds ${fmt(last?.tender.gross ?? 0)}, net ${fmt(last?.tender.netCash ?? 0)}`,
        feeds: 'Tender proceeds, the tax charged on them, and how much value remains locked up.',
        assumption:
          'Tender sales are taxed at the ordinary rate throughout. Pre-IPO RSU tenders genuinely are ordinary income, but option shares held long enough could qualify for capital gains treatment that this path does not grant them.'
      },
      {
        id: 'tenderPriceFrac',
        kind: 'input',
        label: 'Tender price vs FMV',
        plain: offer.optionsEnabled
          ? 'What the tender pays relative to fair market value per share. Secondary buyers usually pay a discount, and 1.0 means no discount at all.'
          : 'What the tender pays relative to the company valuation you forecast. RSU tenders are priced off the valuation path rather than a share price, and 1.0 means no discount at all.',
        formula: offer.optionsEnabled
          ? `tender price = FMV × ${ratio(offer.tenderPriceFrac)}\nyear ${horizon}: ${money(finalFmv)} × ${ratio(offer.tenderPriceFrac)} = ${money(finalFmv * offer.tenderPriceFrac)} per share`
          : `tendered paper is valued at ${ratio(offer.tenderPriceFrac)} of the valuation path\nyear ${horizon}: ${ratio(finalVal)} × ${ratio(offer.tenderPriceFrac)} = ${ratio(finalVal * offer.tenderPriceFrac)}\n  = ${ratio((finalVal * offer.tenderPriceFrac) / grantRef)}× grant-date value`,
        feeds: 'Gross tender proceeds and the tax charged on them.',
        assumption:
          'One discount for every window in every year. In practice the discount widens exactly when the company is struggling, which is the scenario where selling matters most.'
      }
    );
  }

  entries.push(
    {
      id: 'existingEquityValue',
      kind: 'input',
      label: 'Existing vested equity',
      plain:
        'Equity you already hold at this company from prior tenure. It is fully vested on day one and rides the same valuation path as the new grant.',
      formula:
        offer.existingEquityValue > 0
          ? `${fmt(offer.existingEquityValue)} at grant-date valuation\nyear ${horizon} value: ${fmt(offer.existingEquityValue * finalMult)} (${ratio(finalMult)}×)\nsold at ${ratio(offer.existingPoolSellFrac)} per year, gains taxed at ${rate(taxRates.ltcg)}`
          : `${fmt(0)} — only relevant when comparing a stay-put option against outside offers`,
      feeds: 'A separate pool tracked alongside new-grant equity, with its own sale proceeds line.',
      assumption:
        'Cost basis is set equal to the value you enter, so only appreciation from today forward is taxed. Gains you already have on paper are treated as untaxed.'
    },
    {
      id: 'retentionGrant',
      kind: 'input',
      label: 'Retention grant',
      plain:
        'A one-off extra grant, typically the counter-offer from your current employer. It is priced at the valuation in the year it starts, not at the original grant-date valuation.',
      formula:
        (offer.retentionGrant?.amount ?? 0) > 0
          ? `${fmt(offer.retentionGrant!.amount)} starting year ${offer.retentionGrant!.startYr}, over ${offer.retentionGrant!.vestYears} years\n  = ${fmt(offer.retentionGrant!.amount / Math.max(1, offer.retentionGrant!.vestYears))} per year\npriced at the year-${offer.retentionGrant!.startYr} valuation of ${ratio(valPath[offer.retentionGrant!.startYr - 1] ?? grantRef)}`
          : `${fmt(0)} — no retention grant modeled`,
      feeds: 'Additional RSU vesting on top of the initial grant and any refreshes.',
      assumption:
        'Vesting starts immediately in the year you name, with no cliff of its own. Retention grants usually carry their own cliff and stay-conditions, neither of which is modeled here.'
    },
    {
      id: 'refreshAnnualShares',
      kind: 'input',
      label: 'Annual refresh shares',
      plain:
        'New option shares granted each year, struck at that year’s fair market value. Later refreshes have a higher strike, so they are worth less per share than the original grant.',
      formula:
        offer.refreshAnnualShares > 0
          ? `${n(offer.refreshAnnualShares)} shares per year, struck at that year’s FMV\nyear 1 strike ${money(fmvPath[0] ?? offer.currentFMV)} · year ${horizon} strike ${money(finalFmv)}\neach refresh vests over ${offer.optionVestYears} years`
          : `${n(0)} — no option refresh modeled`,
      feeds: 'Option share counts, exercise cost, and the pipeline of unvested option value.',
      assumption:
        'Each refresh is struck at the modeled FMV for its grant year, which assumes the 409A tracks your valuation path exactly. Refreshes granted after a large markup are barely worth anything until the price rises again.'
    },
    {
      id: 'annualDilutionPct',
      kind: 'input',
      label: 'Annual dilution %',
      plain:
        'Yearly reduction in your ownership percentage from new share issuance. It bites into per-share value on top of whatever the valuation path does.',
      formula: offer.dilutionEnabled
        ? `FMV in year y = base FMV × valuation multiple × (1 − ${ratio(offer.annualDilutionPct)})^y\nover ${horizon} years that keeps ${ratio((1 - offer.annualDilutionPct) ** horizon)} of the un-diluted per-share value`
        : `dilution is off — per-share value tracks the valuation path exactly`,
      feeds: 'Share FMV, and through it option value, tender pricing, and held value.',
      assumption:
        'Applied to options only. RSU value tracks the raw valuation path with no dilution haircut, so an options offer and an RSU offer are not treated symmetrically on this point.'
    },
    {
      id: 'donationFrac',
      kind: 'input',
      label: 'Donate fraction',
      plain:
        'Share of each RSU vest given to charity before tax. The donated portion leaves your net worth entirely — it is not income, not taxed, and never enters the sellable pool.',
      formula: !offer.rsuEnabled
        ? `this offer has no RSU grant, so nothing is donated\ndonation matching applies to RSU vests only —\noption grants are untouched by it`
        : offer.donationMatchingEnabled && offer.donationFrac > 0
          ? `donated: ${ratio(Math.min(offer.donationFrac, offer.donationMaxPct))} of each vest, capped at the program limit ${ratio(offer.donationMaxPct)}\nyear ${horizon}: ${fmt(last?.donatedUSD ?? 0)} donated\nemployer adds ${ratio(offer.donationMatchRatio)}× → charity receives ${fmt(last?.charityUSD ?? 0)}\nincome tax avoided at ${rate(taxRates.ord)}: ${fmt(last?.donationTaxSaved ?? 0)}`
          : `donation matching is off — the full vest counts as your income`,
      feeds: 'Reduces taxable vest income and net worth; the charity total and tax avoided are reported separately.',
      assumption:
        'Modeled as an outright gift: the donated value leaves your net worth and comes back only as the tax you did not pay. The tax avoided is reported for information and is not added back into cash.'
    },
    {
      id: 'donationMatchRatio',
      kind: 'input',
      label: 'Match ratio',
      plain: 'How much your employer adds for every dollar you give. It changes what the charity receives; it does not change your own net worth.',
      formula: `charity receives donated × (1 + ${ratio(offer.donationMatchRatio)}) = ${ratio(1 + offer.donationMatchRatio)}× what you give\na ${ratio(offer.donationMatchRatio)} here is a ${ratio(offer.donationMatchRatio)}:1 employer match`,
      feeds: 'The charity total shown in the annual detail tab, and nothing else on the page.'
    },
    {
      id: 'donationMaxPct',
      kind: 'input',
      label: 'Program cap',
      plain: 'The ceiling your employer’s program puts on the donated fraction. Anything you enter above it is clamped down to it.',
      formula: `effective donated fraction = min(${ratio(offer.donationFrac)}, ${ratio(offer.donationMaxPct)}) = ${ratio(Math.min(offer.donationFrac, offer.donationMaxPct))}`,
      feeds: 'Caps the donate fraction before any of the donation math runs.'
    }
  );

  // ── Result tile ──
  entries.push({
    id: 'netWorth',
    kind: 'result',
    label: `${offer.name} — net worth`,
    plain: `Everything ${offer.name} leaves you holding at year ${horizon}: cash you have banked plus equity you still own, valued along your forecast path.`,
    formula: `cumulative cash        ${fmt(cumCash)}\nheld option value      ${fmt(last?.heldVal ?? 0)}\nsellable pool          ${fmt(last?.poolAfter ?? 0)}\nexisting equity pool   ${fmt(last?.existingPoolAfter ?? 0)}\npre-IPO paper          ${fmt(last?.preIpoPaperValue ?? 0)}\n                     = ${fmt(netWorth)}\n\n${(cashShare * 100).toFixed(0)}% of it is cash; ${fmt(illiquid)} is not`,
    feeds: 'The net-worth chart, the leader ranking, and the decision brief.',
    assumption:
      'Not a sum of comparable dollars. Cash is banked and spent; the rest is stock at a price you forecast, some of which you could not sell today at any price. The cash share tile beside it is the honest way to read this number.'
  });

  return entries;
}

/** Explainers for settings that apply to every offer, plus the decision-brief tiles. */
export function getJobOfferGlobalExplainers(ctx: JobOfferGlobalExplainerContext): FieldExplainerContent[] {
  const { settings, taxRates, sensitivity, sensitivityOutcome, sensitivityOfferName, brief, offers } = ctx;
  const horizon = settings.timeHorizon;
  const probTotal = sensitivity.probBear + sensitivity.probBase + sensitivity.probBull;

  const entries: FieldExplainerContent[] = [
    {
      id: 'timeHorizon',
      kind: 'input',
      label: 'Time horizon (years)',
      plain:
        'How many years the comparison runs. It is a hard cut-off, not a window: nothing that happens after the final year counts toward any number on this page.',
      formula: `${horizon} years, applied identically to every offer\nvaluation paths, vest schedules, and tender windows are all truncated here`,
      feeds: 'Every chart axis, every result tile, and which grants finish vesting inside the comparison.',
      assumption: `A grant that vests over more than ${horizon} years is only counted for the portion that vests inside the window. Shortening the horizon does not just discount a slow-vesting offer — it removes the unvested part entirely, which is the fair reading if you would leave at year ${horizon}.`
    },
    {
      id: 'taxPreset',
      kind: 'input',
      label: 'Tax preset',
      plain:
        'Picks five blended rates covering ordinary income, capital gains, ISO exercise, AMT, and cash comp. They are top-of-bracket approximations for a high earner, not a return calculation.',
      formula: `ordinary            ${rate(taxRates.ord)}\nlong-term gains     ${rate(taxRates.ltcg)}\nISO state + city    ${rate(taxRates.isoStateCity)}\nISO federal AMT     ${rate(taxRates.isoAmt)}\ncash effective      ${rate(taxRates.cashEff)}`,
      feeds: 'Every tax line on the page: RSU vests, the IPO recognition event, option exercise, tender sales, and pool sales.',
      assumption:
        'Flat rates with no brackets, no standard deduction, and no income thresholds where a deduction or credit is withdrawn. They are calibrated to someone already at the top of the schedule; at a more ordinary income they overstate tax, and they overstate it for every offer equally.'
    },
    {
      id: 'filingStatus',
      kind: 'input',
      label: 'Filing status',
      plain:
        'A coarse adjustment, not a separate rate table. Married filing jointly simply shaves four points off the ordinary and cash rates; capital gains and AMT rates do not move.',
      formula: `single → preset rates as published\nmarried filing jointly → ordinary and cash effective each − 4 points\n\nselected: ${settings.filingStatus === 'married_joint' ? 'married filing jointly' : 'single'}\nordinary ${rate(taxRates.ord)} · cash ${rate(taxRates.cashEff)}`,
      feeds: 'The resolved rates used by every offer.',
      assumption:
        'The four-point shift stands in for wider joint brackets. It is a single number for every income level, so it is roughly right in the middle of the range and wrong at both ends.'
    },
    {
      id: 'npvDiscountRate',
      kind: 'input',
      label: 'Discount rate',
      plain:
        'The annual rate used to discount cash flows back to today when NPV is switched on. Year-one dollars are worth more than year-five dollars, and a higher rate widens that gap.',
      formula: settings.npvEnabled
        ? `each year is discounted at (1 + ${ratio(settings.npvDiscountRate)})^(y − 0.5)\nthe half-year offset treats cash as arriving mid-year\nyear ${horizon} cash is worth ${(100 / Math.pow(1 + settings.npvDiscountRate, horizon - 0.5)).toFixed(0)}% of face`
        : `NPV is off — cash is summed at face value regardless of when it arrives`,
      feeds: 'The NPV figure in each offer’s result note. It never changes net worth, only the cash comparison.',
      assumption:
        'Discounting is applied to cash only. Equity value is never discounted, even though it is further away and far less certain, so NPV makes cash-heavy offers look relatively worse than they are.'
    },
    {
      id: 'bearMult',
      kind: 'input',
      label: 'Bear multiplier',
      plain:
        'Scales the whole valuation path down to build a downside case. Every year is multiplied by the same number, so the shape of your forecast is kept and only the level moves.',
      formula: sensitivityOutcome
        ? `every year of the valuation path × ${ratio(sensitivity.bearMult)}\n${sensitivityOfferName ?? 'selected offer'} net worth: ${fmt(sensitivityOutcome.baseNW)} → ${fmt(sensitivityOutcome.bearNW)}\ngiveback ${fmt(sensitivityOutcome.baseNW - sensitivityOutcome.bearNW)}`
        : `every year of the valuation path × ${ratio(sensitivity.bearMult)}`,
      feeds: 'The bear bar in the sensitivity chart and the bear-case giveback tile.',
      assumption:
        'Only the valuation moves. Salary, bonus, refreshes, and tender availability are all held steady, which is optimistic: the world where the valuation halves is usually also the world where refreshes shrink and tenders stop.'
    },
    {
      id: 'bullMult',
      kind: 'input',
      label: 'Bull multiplier',
      plain: 'The same scaling in the other direction — your upside case, applied uniformly across every year of the path.',
      formula: sensitivityOutcome
        ? `every year of the valuation path × ${ratio(sensitivity.bullMult)}\n${sensitivityOfferName ?? 'selected offer'} net worth: ${fmt(sensitivityOutcome.baseNW)} → ${fmt(sensitivityOutcome.bullNW)}`
        : `every year of the valuation path × ${ratio(sensitivity.bullMult)}`,
      feeds: 'The bull bar in the sensitivity chart and the expected-value calculation.',
      assumption:
        'Because taxes here are flat rates, upside scales almost linearly. A real windfall of this size would be taxed harder than the model charges it.'
    },
    {
      id: 'probBear',
      kind: 'input',
      label: 'P(bear)',
      plain: 'Your weight on the downside case. The three probabilities are normalised by their total, so they do not have to add to one.',
      formula: sensitivityOutcome
        ? `weights: bear ${ratio(sensitivity.probBear)} · base ${ratio(sensitivity.probBase)} · bull ${ratio(sensitivity.probBull)} (total ${ratio(probTotal)})\nexpected = (${fmt(sensitivityOutcome.bearNW)}×${ratio(sensitivity.probBear)} + ${fmt(sensitivityOutcome.baseNW)}×${ratio(sensitivity.probBase)} + ${fmt(sensitivityOutcome.bullNW)}×${ratio(sensitivity.probBull)}) / ${ratio(probTotal)}\n  = ${fmt(sensitivityOutcome.expectedNW)}`
        : `weights ${ratio(sensitivity.probBear)} / ${ratio(sensitivity.probBase)} / ${ratio(sensitivity.probBull)}`,
      feeds: 'The expected-value figure in the sensitivity tab.',
      assumption:
        'Three points are not a distribution. An expected value built from bear, base, and bull hides the fact that startup outcomes are heavily skewed, with most of the probability mass well below the base case.'
    },
    {
      id: 'probBase',
      kind: 'input',
      label: 'P(base)',
      plain: 'Your weight on the valuation path exactly as you typed it into the offer card.',
      formula: `base case uses the unscaled path\nweight ${ratio(sensitivity.probBase)} of ${ratio(probTotal)} = ${((sensitivity.probBase / (probTotal || 1)) * 100).toFixed(0)}% of the expected value`,
      feeds: 'The expected-value figure in the sensitivity tab.'
    },
    {
      id: 'probBull',
      kind: 'input',
      label: 'P(bull)',
      plain: 'Your weight on the upside case. Worth setting deliberately low: it is the number people are least honest with themselves about.',
      formula: `weight ${ratio(sensitivity.probBull)} of ${ratio(probTotal)} = ${((sensitivity.probBull / (probTotal || 1)) * 100).toFixed(0)}% of the expected value`,
      feeds: 'The expected-value figure in the sensitivity tab.'
    }
  ];

  if (settings.taxPreset === 'custom') {
    const customRates: Array<[keyof TaxRates, string]> = [
      ['ord', 'Ordinary (0-1)'],
      ['ltcg', 'LTCG (0-1)'],
      ['isoStateCity', 'ISO state+city (0-1)'],
      ['isoAmt', 'ISO federal AMT (0-1)'],
      ['cashEff', 'Cash effective (0-1)']
    ];
    const applies: Record<string, string> = {
      ord: 'RSU vests, the IPO recognition event, NSO spread at exercise, and every tender sale',
      ltcg: 'sales from the post-liquidity pool and from any existing vested equity',
      isoStateCity: 'the ISO spread at exercise on the regular-tax path',
      isoAmt: 'the ISO spread at exercise on the AMT path — the model charges whichever of the two is higher',
      cashEff: 'salary, bonus, and signing bonus'
    };
    for (const [key, label] of customRates) {
      entries.push({
        id: `custom-${key}`,
        kind: 'input',
        label,
        plain: `Your own ${RATE_LABELS[key]} rate, entered as a decimal between 0 and 1. It replaces the preset value everywhere that rate is used.`,
        formula: `${ratio(taxRates[key])} = ${rate(taxRates[key])}\napplied to ${applies[key]}${settings.filingStatus === 'married_joint' && (key === 'ord' || key === 'cashEff') ? `\nmarried filing jointly has already taken 4 points off this` : ''}`,
        feeds: 'Every calculation that touches this rate, for all offers at once.',
        assumption:
          key === 'cashEff'
            ? 'This is an effective rate across your whole cash comp, not a marginal one. Using your top bracket here will understate take-home considerably.'
            : 'A single flat rate for the whole horizon. It does not step up as a large recognition event pushes you into higher brackets.'
      });
    }
  }

  if (brief) {
    const leadPct = brief.winnerNetWorth > 0 ? (brief.spread / brief.winnerNetWorth) * 100 : null;
    entries.push(
      {
        id: 'currentLeader',
        kind: 'result',
        label: 'Current leader',
        plain: `Whichever offer has the highest modeled net worth at year ${horizon}. It is a ranking on one number, and that number leans on your valuation forecast.`,
        formula: `${offers.map((o) => `${o.name}  ${fmt(o.netWorth)}`).join('\n')}\n\nleader: ${brief.winnerName}${brief.runnerUpName ? `\nahead of ${brief.runnerUpName} by ${fmt(brief.spread)}${leadPct !== null ? ` (${leadPct.toFixed(1)}% of its own total)` : ''}` : ''}`,
        feeds: 'The decision brief summary and which offer the bear-case giveback is measured against.',
        assumption:
          'A lead built out of equity can vanish under a small change to the valuation path. Compare the spread above against the bear-case giveback tile before treating this as a decision.'
      },
      {
        id: 'leaderCashShare',
        kind: 'result',
        label: 'Leader cash share',
        plain:
          'How much of the leader’s modeled net worth is cash already banked rather than stock still at risk. The higher this is, the less the ranking depends on a forecast.',
        formula: `cumulative cash / net worth\n  = ${fmt(brief.winnerCumCash)} / ${fmt(brief.winnerNetWorth)}\n  = ${(brief.winnerCashShare * 100).toFixed(1)}%`,
        feeds: 'The tone of the decision brief: below 55% it flags the lead as forecast-dependent.',
        assumption:
          'Cash here means every dollar that has passed through your hands after tax, including proceeds from selling stock. It does not mean money you still have — spending is not modeled.'
      },
      {
        id: 'leaderIlliquid',
        kind: 'result',
        label: 'Leader illiquid value',
        plain:
          'The part of the leader’s net worth that is still stock: unexercised options, the sellable pool you chose not to sell, and pre-IPO paper you cannot sell at all.',
        formula: `net worth − cumulative cash\n  = ${fmt(brief.winnerIlliquid)}\nthis is the amount exposed to your valuation path`,
        feeds: 'Nothing downstream. It is the counterweight to the headline net-worth number.',
        assumption:
          'Everything in here is priced at your forecast valuation, and some of it has no market at that price today. Pre-IPO paper in particular is worth exactly nothing until a liquidity event you have assumed will happen.'
      },
      {
        id: 'bearGiveback',
        kind: 'result',
        label: 'Bear-case giveback',
        plain:
          'How much of the leader’s headline net worth disappears in your bear scenario. It only appears when the sensitivity panel is pointed at the leading offer.',
        formula:
          brief.downsideGap !== null && sensitivityOutcome
            ? `base ${fmt(sensitivityOutcome.baseNW)} − bear ${fmt(sensitivityOutcome.bearNW)}\n  = ${fmt(brief.downsideGap)}\n${brief.runnerUpName ? `lead over ${brief.runnerUpName} is ${fmt(brief.spread)}, so the bear case ${brief.downsideGap > brief.spread ? 'more than erases it' : 'does not erase it'}` : ''}`
            : `point the sensitivity panel at ${brief.winnerName} to see this\nit compares the base case against the bear multiplier`,
        feeds: 'The closing line of the decision brief.',
        assumption:
          'The giveback is measured against one scaled valuation path, not a real downside. It says nothing about the offer being withdrawn, the company failing, or you leaving before the cliff.'
      }
    );
  }

  return entries;
}
